/**
 * Controla el vaso superior: arrastrarlo horizontal/diagonalmente lo
 * inclina, y al superar un ángulo mínimo empieza a verter un chorro de
 * bolas con un pequeño delay natural entre una y otra (no todas de golpe).
 *
 * EXCEPCIÓN DELIBERADA a la regla "sin Phaser en plinko/logic": este
 * archivo maneja input de puntero y tweens, así que sí importa Phaser.
 */

import Phaser from 'phaser';
import { TEX } from '../core/TextureFactory';

/** Ángulo (grados) a partir del cual el vaso empieza a verter. */
const POUR_TILT_DEG = 22;
/** Inclinación máxima permitida del vaso, en grados. */
const MAX_TILT_DEG = 65;
/** Delay entre bola y bola del chorro cuando el vaso está bien inclinado, en ms. */
const MIN_POUR_DELAY_MS = 45;
const MAX_POUR_DELAY_MS = 140;

export interface PourControllerOptions {
  scene: Phaser.Scene;
  /** Punto de reposo del vaso (boca del vaso, coordenadas de pantalla). */
  restX: number;
  restY: number;
  /** Llamado repetidamente mientras el vaso vierte, una vez por bola. */
  onPourBall: (spawnX: number, spawnY: number, vx: number, vy: number) => boolean;
}

/**
 * Vaso superior arrastrable. `onPourBall` decide si realmente puede salir
 * una bola (p.ej. si ya no quedan bolas o el pool está lleno) devolviendo
 * `false` para que el controlador pause el vertido sin destruirse.
 */
export class PourController {
  private readonly scene: Phaser.Scene;
  private readonly restX: number;
  private readonly restY: number;
  private readonly onPourBall: PourControllerOptions['onPourBall'];

  readonly cupImage: Phaser.GameObjects.Image;
  private pouring = false;
  private pourTimer?: Phaser.Time.TimerEvent;
  private dragging = false;
  private enabled = true;

  constructor(options: PourControllerOptions) {
    this.scene = options.scene;
    this.restX = options.restX;
    this.restY = options.restY;
    this.onPourBall = options.onPourBall;

    // Zona táctil ≥44px (criterio de accesibilidad del plan): la textura del
    // vaso es más chica (32x36), así que se agranda el área de arrastre sin
    // tocar el tamaño visual. Rectángulo en el espacio local del frame
    // (origen 0.5, 0 → x=0 es el centro, y=0 es el borde superior visual).
    const TOUCH_TARGET = 44;
    this.cupImage = this.scene.add
      .image(this.restX, this.restY, TEX.cup)
      .setOrigin(0.5, 0)
      .setInteractive({
        useHandCursor: true,
        draggable: true,
        hitArea: new Phaser.Geom.Rectangle(-TOUCH_TARGET / 2, -4, TOUCH_TARGET, TOUCH_TARGET),
        hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      });

    this.cupImage.on('drag', this.handleDrag, this);
    this.cupImage.on('dragstart', this.handleDragStart, this);
    this.cupImage.on('dragend', this.handleDragEnd, this);
  }

  private handleDragStart(): void {
    if (!this.enabled) return;
    this.dragging = true;
  }

  private handleDrag(_pointer: Phaser.Input.Pointer, dragX: number, dragY: number): void {
    if (!this.enabled || !this.dragging) return;

    const dx = dragX - this.restX;
    const dy = dragY - this.restY;
    // el arrastre puede ser horizontal o diagonal; el ángulo hacia abajo/costado inclina el vaso.
    const rawDeg = Phaser.Math.RadToDeg(Math.atan2(dx, Math.max(dy, 1)));
    const tiltDeg = Phaser.Math.Clamp(rawDeg, -MAX_TILT_DEG, MAX_TILT_DEG);

    this.cupImage.setRotation(Phaser.Math.DegToRad(tiltDeg));
    // el vaso sigue un poco el arrastre para dar sensación de peso, sin despegarse de su sitio.
    this.cupImage.setPosition(
      this.restX + Phaser.Math.Clamp(dx, -40, 40) * 0.35,
      this.restY + Phaser.Math.Clamp(Math.max(dy, 0), 0, 30) * 0.3
    );

    if (Math.abs(tiltDeg) >= POUR_TILT_DEG) {
      this.startPouring(Math.sign(tiltDeg), Math.abs(tiltDeg));
    } else {
      this.stopPouring();
    }
  }

  private handleDragEnd(): void {
    this.dragging = false;
    this.stopPouring();
    this.resetCup();
  }

  private resetCup(): void {
    this.scene.tweens.add({
      targets: this.cupImage,
      x: this.restX,
      y: this.restY,
      rotation: 0,
      duration: 220,
      ease: 'Back.Out',
    });
  }

  private startPouring(direction: number, tiltDeg: number): void {
    if (this.pouring) return;
    this.pouring = true;
    const intensity = Phaser.Math.Clamp((tiltDeg - POUR_TILT_DEG) / (MAX_TILT_DEG - POUR_TILT_DEG), 0, 1);
    const delay = Phaser.Math.Linear(MAX_POUR_DELAY_MS, MIN_POUR_DELAY_MS, intensity);

    this.pourTimer = this.scene.time.addEvent({
      delay,
      loop: true,
      callback: () => this.emitOneBall(direction, intensity),
    });
  }

  private emitOneBall(direction: number, intensity: number): void {
    const mouthOffset = 14 * direction;
    const spawnX = this.cupImage.x + mouthOffset;
    const spawnY = this.cupImage.y + 20;
    const vx = direction * (1 + intensity * 2);
    const vy = 1 + intensity;
    const spawned = this.onPourBall(spawnX, spawnY, vx, vy);
    if (!spawned) this.stopPouring();
  }

  private stopPouring(): void {
    if (!this.pouring) return;
    this.pouring = false;
    this.pourTimer?.remove();
    this.pourTimer = undefined;
  }

  /**
   * Solo para pruebas/e2e: dispara un arrastre sintético a inclinación máxima
   * para verter todas las bolas sin interacción real del jugador. Usa el mismo
   * camino que un arrastre de verdad (dragstart → drag inclinado sostenido).
   */
  debugPour(): void {
    if (!this.enabled) return;
    this.dragging = true;
    this.handleDrag({} as Phaser.Input.Pointer, this.restX + 120, this.restY + 20);
  }

  /** Deshabilita el arrastre (p.ej. cuando ya no quedan bolas por verter). */
  setEnabled(value: boolean): void {
    this.enabled = value;
    if (!value) {
      this.stopPouring();
      this.dragging = false;
      this.resetCup();
    }
  }

  destroy(): void {
    this.stopPouring();
    this.cupImage.destroy();
  }
}
